import type { Document, Element as XMLElement } from '@borgar/simple-xml';
import type { Theme, ThemeColorScheme, ThemeCustomColor, ThemeFontCollection } from '@jsfkit/types';
import { readDrawingMLColor } from '../color/readDrawingMLColor.ts';
import { attr } from '../utils/attr.ts';
import { getFirstChild } from '../utils/getFirstChild.ts';
import type { ConversionContext } from '../ConversionContext.ts';
import { addProp } from '../utils/addProp.ts';
import { THEMES } from '@jsfkit/utils';

const DEFAULT_FONT = 'Aptos Narrow';

/**
 * Extracts a font collection from the given OOXML element, which should be either a `majorFont` or
 * `minorFont` element.
 *
 * The font collection must contain a `latin` element (although we include a fallback), plus
 * optional `ea` (east Asian) and `cs` (complex script) elements. These three specify a typeface to
 * use for that script.
 */
function extractFontCollection (fontCollection: XMLElement) {
  const latin = getFirstChild(fontCollection, 'latin');
  const eastAsian = getFirstChild(fontCollection, 'ea');
  const complexScript = getFirstChild(fontCollection, 'cs');

  const font: ThemeFontCollection = {
    latin: {
      typeface: latin ? attr(latin, 'typeface', DEFAULT_FONT) : DEFAULT_FONT,
    },
  };
  if (eastAsian) {
    const typeface = attr(eastAsian, 'typeface');
    if (typeface) {
      font.eastAsian = { typeface };
    }
  }
  if (complexScript) {
    const typeface = attr(complexScript, 'typeface');
    if (typeface) {
      font.complexScript = { typeface };
    }
  }
  return font;
}

export function handlerTheme (dom: Document, context: ConversionContext): Theme {
  const theme: Theme = structuredClone(THEMES[context.defaultThemeVersion] ?? THEMES.default);

  // get a derivative of context but use our new theme
  const ctx = Object.create(context);
  ctx.theme = theme;

  const themeElement = dom.querySelector('theme');
  if (!themeElement) {
    // exit early if we have no theme
    return theme;
  }
  const themeName = attr(themeElement, 'name');
  if (themeName) {
    theme.name = themeName;
  }

  const themeElements = themeElement.querySelector('themeElements');

  const clrScheme = getFirstChild(themeElements, 'clrScheme');
  const clrSchemeName = clrScheme ? attr(clrScheme, 'name') : '';
  if (clrSchemeName) {
    theme.colorScheme.name = clrSchemeName;
  }
  clrScheme?.children.forEach(child => {
    const key = child.tagName;
    const colorElm = child.children[0];
    if (key in theme.colorScheme && colorElm) {
      const color = readDrawingMLColor(colorElm);
      if (color) {
        addProp(theme.colorScheme, key as keyof ThemeColorScheme, color);
      }
    }
  });

  const fontScheme = getFirstChild(themeElements, 'fontScheme');
  const fontSchemeName = fontScheme ? attr(fontScheme, 'name') : '';
  if (fontSchemeName) {
    theme.fontScheme.name = fontSchemeName;
  }
  fontScheme?.children.forEach(d => {
    if (d.tagName === 'majorFont') {
      theme.fontScheme.major = extractFontCollection(d);
    }
    else if (d.tagName === 'minorFont') {
      theme.fontScheme.minor = extractFontCollection(d);
    }
  });

  // const fmtScheme = getFirstChild(themeElements, 'fmtScheme');

  // const fillStyleLst = getFirstChild(fmtScheme, 'fillStyleLst');
  // const fillList = [];
  // fillStyleLst?.children.forEach(d => {
  //   const fill = readFill(d, ctx);
  //   if (fill) { fillList.push(fill); }
  // });
  // theme.fillList = fillList;

  // const bgFillStyleLst = getFirstChild(fmtScheme, 'bgFillStyleLst');
  // const bgFillList = [];
  // bgFillStyleLst?.children.forEach(d => {
  //   const fill = readFill(d, ctx);
  //   if (fill) { bgFillList.push(fill); }
  // });
  // theme.bgFillList = bgFillList;

  // const lnStyleLst = getFirstChild(fmtScheme, 'lnStyleLst');
  // const lineList = [];
  // lnStyleLst?.children.forEach(d => {});
  // theme.lineList = lineList;

  // const effectStyleLst = getFirstChild(fmtScheme, 'effectStyleLst');
  // const effectList = [];
  // effectStyleLst?.children.forEach(d => {});
  // theme.effectList = effectList;

  // const objectDefaults = dom.querySelector('theme > objectDefaults');

  const custClrLst = getFirstChild(themeElement, 'custClrLst');
  if (custClrLst) {
    const customColors: ThemeCustomColor[] = [];
    custClrLst.children.forEach(custClr => {
      const colorElm = custClr.children[0];
      const color = colorElm ? readDrawingMLColor(colorElm) : null;
      if (color) {
        const entry: ThemeCustomColor = { color };
        const name = attr(custClr, 'name');
        if (name) {
          entry.name = name;
        }
        customColors.push(entry);
      }
    });
    if (customColors.length > 0) {
      theme.customColors = customColors;
    }
  }

  return theme;
}
